import { Box, Button } from '@mui/material';
import React, { PropsWithChildren, useEffect, useState } from 'react';
import { type Layout, ReactGridLayout, useContainerWidth } from 'react-grid-layout';

type GridConfig = {
    /** Number of columns in the grid (default: 12) */
    cols: number;
    /** Height of a single row in pixels (default: 150) */
    rowHeight: number;
    /** [horizontal, vertical] margin between items in pixels (default: [10, 10]) */
    margin: readonly [number, number];
    /** [horizontal, vertical] padding inside the container (default: null, uses margin) */
    containerPadding: readonly [number, number] | null;
    /** Maximum number of rows (default: Infinity) */
    maxRows: number;
};
type ReactGridContainerProps = {
    localStorageName: string;
    layoutProps: Layout;
    gridconfig?: Partial<GridConfig>;
    currentXIndexCard?: React.MutableRefObject<number>;
    currentYIndexCard?: React.MutableRefObject<number>;
    initialXIndexCard?: number;
};

/**
 *
 * exemple layout
 * const layout: Layout = [
 *      { i: 'a', x: 0, y: 0, w: 1, h: 1, isResizable: true, resizeHandles: ['s', 'w', 'e', 'n', 'sw', 'nw', 'se', 'ne'] },
 *      { i: 'd', x: 1, y: 3, w: 1, h: 4, isResizable: true, resizeHandles: ['s'] },
 *      { i: 'b', x: 2, y: 1, w: 1, h: 2, isResizable: false},
 *      { i: 'c', x: 3, y: 0, w: 1, h: 2 },
 *  ];
 * @param param0
 */
const ReactGridContainer: React.FunctionComponent<PropsWithChildren<ReactGridContainerProps>> =
    function ({
        localStorageName,
        layoutProps,
        gridconfig = { cols: 6, rowHeight: 40 },
        currentXIndexCard,
        initialXIndexCard = -1,
        currentYIndexCard,
        children,
    }) {
        const { width, containerRef, mounted } = useContainerWidth();
        const [layout, setLayout] = useState<Layout>();

        // CHeck if layout is stored in the local storage on mount
        useEffect(() => {
            if (layoutProps && layoutProps.length > 0) {
                const storedLayout = localStorage.getItem(localStorageName);

                if (storedLayout !== null) {
                    // load from
                    setLayout(JSON.parse(storedLayout));
                } else if (localStorage.getItem(localStorageName) !== JSON.stringify(layoutProps)) {
                    setLayout(layoutProps);
                    localStorage.setItem(localStorageName, JSON.stringify(layoutProps));
                }
            }
            if (currentXIndexCard && currentXIndexCard.current) {
                // eslint-disable-next-line no-param-reassign
                currentXIndexCard.current = initialXIndexCard;
            }

            if (currentYIndexCard && currentYIndexCard.current) {
                // eslint-disable-next-line no-param-reassign
                currentYIndexCard.current = 0;
            }

            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [localStorageName, layoutProps]);

        // On change store the new layout in localStorage
        const handleLayoutChange = (layoutParam: Layout) => {
            if (layoutParam) {
                if (localStorage.getItem(localStorageName) !== JSON.stringify(layoutParam)) {
                    localStorage.setItem(localStorageName, JSON.stringify(layoutParam));
                }
            }
        };

        const handleResetLayout = () => {
            if (layoutProps && layoutProps.length > 0) {
                setLayout(layoutProps);
                localStorage.setItem(localStorageName, JSON.stringify(layoutProps));
            }
        };

        return (
            <Box
                sx={{ width: '100%', position: 'relative', padding: '16px 0px' }}
                ref={containerRef}
            >
                <Button
                    sx={{ position: 'absolute', right: 0, top: '-16px', zIndex: 1 }}
                    onClick={handleResetLayout}
                >
                    réinitialiser
                </Button>
                {mounted && layout && (
                    <ReactGridLayout
                        layout={layout}
                        width={width}
                        gridConfig={gridconfig}
                        onLayoutChange={handleLayoutChange}
                    >
                        {children}
                    </ReactGridLayout>
                )}
            </Box>
        );
    };

export default ReactGridContainer;
